/**
 * Real API Dataset Service — calls backend via cross-origin api.<domain> (ALI-protected)
 */
import type { Dataset, DatasetStatus, PaginatedResponse, DatasetConfig } from '../../../types/starnexus';
import type { DatasetFilters, DatasetSortConfig } from '../mock/mockDatasetService';
import { apiUrl, apiFetch } from '../../../utils/apiClient';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await apiFetch(apiUrl(path), {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options?.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `API error ${res.status}`);
  }
  return res.json();
}

export const realDatasetService = {
  async listDatasets(
    filters?: DatasetFilters,
    _sort?: DatasetSortConfig,
    page = 1,
    pageSize = 20,
  ): Promise<PaginatedResponse<Dataset>> {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (filters?.search) params.set('search', filters.search);
    if (filters?.teamId) params.set('teamId', filters.teamId);
    if (filters?.status) params.set('status', filters.status);
    if (filters?.processingType) params.set('processingType', filters.processingType);
    return request(`/datasets?${params}`);
  },

  async getDataset(datasetId: string): Promise<Dataset | null> {
    try { return await request(`/datasets/${datasetId}`); }
    catch { return null; }
  },

  async createDataset(config: DatasetConfig, createdBy: { userId: string; name: string; email: string }): Promise<Dataset> {
    return request('/datasets', { method: 'POST', body: JSON.stringify({ config, createdBy }) });
  },

  async updateDataset(datasetId: string, updates: Partial<DatasetConfig>): Promise<Dataset | null> {
    try { return await request(`/datasets/${datasetId}`, { method: 'PUT', body: JSON.stringify(updates) }); }
    catch { return null; }
  },

  async updateStatus(datasetId: string, status: DatasetStatus): Promise<Dataset | null> {
    try { return await request(`/datasets/${datasetId}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }); }
    catch { return null; }
  },

  async deleteDataset(datasetId: string): Promise<boolean> {
    try { await request(`/datasets/${datasetId}`, { method: 'DELETE' }); return true; }
    catch { return false; }
  },

  async bulkUpdateStatus(ids: string[], status: DatasetStatus): Promise<{ success: string[]; failed: string[] }> {
    return request('/datasets/bulk-status', { method: 'POST', body: JSON.stringify({ datasetIds: ids, status }) });
  },

  async bulkDelete(ids: string[]): Promise<{ success: string[]; failed: string[] }> {
    return request('/datasets/bulk-delete', { method: 'POST', body: JSON.stringify({ datasetIds: ids }) });
  },

  reset(): void { /* no-op for real service */ },
};
